import { toast } from "@/components/common/Toast"
import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query"
import { apiClientV2 } from "../../../services/api/apiClient"
import { getApiErrorCode, getApiErrorStatus, toastApiError } from "./apiError"
import type { NoteOut } from "./notesApi"
import type { PaginatedOut } from "./types/masterData"

const BASE = "/material-compliance/supplier-inquiries"

// ─── Wire shapes (apiClientV2 converts snake_case → camelCase) ──────────────────

export type InquiryBucket = "draft" | "open" | "closed"

const INQUIRY_BUCKETS: readonly InquiryBucket[] = ["draft", "open", "closed"]

/**
 * Coerce an untrusted value (e.g. a URL query param) to a valid bucket,
 * falling back to "open" so we never send the backend an unknown bucket.
 */
export function toInquiryBucket(
  value: string | null | undefined,
): InquiryBucket {
  return INQUIRY_BUCKETS.includes(value as InquiryBucket)
    ? (value as InquiryBucket)
    : "open"
}

// Mirrors backend InquiryTrigger (mc_constants.py) / VALID_INQUIRY_TRIGGERS
// (mc_supplier_inquiries.py) — the backend 400s on an unknown trigger_type.
const INQUIRY_TRIGGERS: readonly string[] = [
  "manual",
  "customer_gap",
  "stale_data",
  "svhc_update",
  "declaration_review",
]

/**
 * Coerce an untrusted value (e.g. a URL query param) to a valid trigger
 * type, falling back to undefined (no filter) so a stale bookmark or
 * hand-edited URL never sends the backend an unknown trigger_type — same
 * convention as toInquiryBucket.
 */
export function toInquiryTrigger(
  value: string | null | undefined,
): string | undefined {
  return value && INQUIRY_TRIGGERS.includes(value) ? value : undefined
}

export type InquiryStatus =
  | "draft"
  | "waiting"
  | "reminded"
  | "escalated"
  | "ureview"
  | "answered"
  // Manually closed from any state (deliberate stop, distinct from "answered").
  | "closed"
  // SES reported a hard bounce — the address can't receive; fix it and resend.
  | "undeliverable"
  // SES reported a spam complaint — delivered, but the recipient flagged it as spam.
  // Do NOT resend (re-emailing a complainer harms sender reputation); follow up manually.
  | "complained"

// Fine-grained statuses the supplier dashboard deep-links to (e.g.
// ?status=waiting,reminded,escalated,ureview) — distinct from the coarse
// buckets above. Also the option list for the Open tab's status filter
// facet (these are exactly the statuses that fall under the "open" bucket).
export const FINE_STATUSES: readonly InquiryStatus[] = [
  "waiting",
  "reminded",
  "escalated",
  "ureview",
  "undeliverable",
  "complained",
]

// F28 — mirrors backend OUTSTANDING_INQUIRY_STATUSES (mc_constants.py): the
// set the supplier dashboard's Outstanding widget counts and drills into.
// Single source for both the widget's "N / total" count and its "Show all"
// link, so a bucket added to one side can never silently drift from the other.
export const OUTSTANDING_INQUIRY_STATUSES: readonly InquiryStatus[] = [
  "waiting",
  "reminded",
  "escalated",
  "ureview",
]

/**
 * Parse a `?status=` URL value as a comma list of fine statuses.
 * Returns null if the value is a bucket (or anything else unrecognized),
 * so callers can fall back to `toInquiryBucket`.
 */
export function toFineStatusList(
  value: string | null | undefined,
): InquiryStatus[] | null {
  if (!value) return null
  const parts = value.split(",").map((s) => s.trim())
  return parts.every((p) => (FINE_STATUSES as readonly string[]).includes(p))
    ? (parts as InquiryStatus[])
    : null
}

/**
 * Whether the org's inquiry template settings could be resolved for this read.
 * `configured`/`unconfigured` = a real state (unconfigured still sends on placeholder
 * branding); `unavailable` = the settings read genuinely FAILED (transient) — the
 * composer shows a retryable error rather than silently blank fields/preview.
 */
export type InquirySettingsStatus =
  | "configured"
  | "unconfigured"
  | "unavailable"

/** McPill tones the server sends on list items. */
export type InquiryPillTone =
  | "muted"
  | "info"
  | "attention"
  | "danger"
  | "success"

/** Where a draft's regulations come from: picked for it, or Settings › Regelwerke. */
export type InquiryScopeSource = "explicit" | "default"

/** How an inquiry's regulation scope reads, decided server-side
 *  (`mc_inquiry_regulation_scope.regulation_scope`): `picked` / `frozen` = exactly
 *  `regulationKeys`; `org_default` = a draft following Settings (no keys on list rows);
 *  `all` = sent before per-inquiry scopes, the full documents; `none` = sent
 *  without documents. */
export type InquiryRegulationScope =
  | "picked"
  | "org_default"
  | "frozen"
  | "all"
  | "none"

export interface SupplierInquiryListItem {
  id: string
  reference: string | null
  supplierId: string | null
  supplierName: string | null
  status: InquiryStatus
  bucket: InquiryBucket
  statusLabel: string
  statusTone: InquiryPillTone
  frameworks: string[]
  /** One canonical `mc_framework_codes` code per `frameworks` entry, same
   *  order/length — see `Declaration.frameworkCodes` (api/types.ts). */
  frameworkCodes?: string[]
  /** What the row's documents actually ask (framework keys, e.g. `REACH_SVHC`): a draft
   *  from its pick or the org's Settings > Regelwerke, a sent inquiry from its frozen
   *  scope. `[]` = no documents (question-only inquiry); `null`/absent = unknown, read
   *  `regulationScope`. Never render `frameworks` for what an inquiry asks. */
  askedRegulations?: string[] | null
  /** The stored scope as canonical framework keys (`PFAS`, `REACH_SVHC`): a draft's
   *  own pick or a sent inquiry's frozen set; null when nothing is stored. */
  regulationKeys?: string[] | null
  scopeSource?: InquiryScopeSource | null
  regulationScope?: InquiryRegulationScope | null
  triggerType: string | null
  triggerDetail: string | null
  dueAt: string | null
  sentAt: string | null
  // apiClientV2 revives createdAt/updatedAt keys into Date instances on response.
  createdAt: Date | null
  // A supplier reply is on the timeline but nothing moved the inquiry out of a
  // remindable status (text-only reply, unreadable attachment, held document).
  hasUnprocessedReply: boolean
  // An open inquiry carries a supplier reply nobody answered or marked done yet.
  hasOpenSupplierMessage: boolean
}

export interface SupplierInquiryStats {
  drafts: number
  open: number
  closed: number
  total: number
}

/** One file on a candidate's email. `declarationId` is set once it became a declaration. */
export interface InquiryMatchAttachment {
  filename: string | null
  contentType: string | null
  fileUrl: string | null
  declarationId: string | null
  declarationNumber: string | null
}

/** Why an inquiry was suggested. `code` picks the localized sentence; `detail` fills it. */
export interface InquiryMatchReason {
  code:
    | "reference_token"
    | "supplier_domain"
    | "supplier_linked"
    | "only_open_for_supplier"
  detail: string | null
}

export interface InquiryMatchSuggestion {
  inquiryId: string
  reference: string | null
  supplierName: string | null
  status: InquiryStatus
  // English label from the backend; render INQUIRY_STATUS_DISPLAY[status] instead.
  statusLabel: string
  sentAt: string | null
  dueAt: string | null
  reasons: InquiryMatchReason[]
}

export interface InquiryMatchCandidate {
  /** The inbound email's id — a candidate is always an email. */
  candidateId: string
  supplierId: string | null
  supplierName: string | null
  sender: string | null
  fileName: string | null
  fileUrl: string | null
  subject: string | null
  bodyPreview: string | null
  /** Full body — the review panel shows it whole; the list uses `bodyPreview`. */
  body: string | null
  recipientsTo: string[]
  attachments: InquiryMatchAttachment[]
  suggestions: InquiryMatchSuggestion[]
  receivedAt: Date
}

interface InquiryMatchCandidateResponse
  extends Omit<InquiryMatchCandidate, "receivedAt"> {
  receivedAt: string
}

export type InquiryTimelineTone = "ok" | "warn" | "neutral"

/** One file on the email behind a timeline step. */
export interface TimelineEmailAttachment {
  filename: string | null
  contentType: string | null
  s3Key: string | null
  /** Short-lived URL the backend includes for previewing / downloading. */
  fileUrl: string | null
}

/**
 * An earlier message in the same thread, shown beneath an outbound timeline email
 * so "View email" surfaces the prior conversation (outbound bodies are stored
 * standalone — threading is header-only — so a reminder has no chain of its own).
 */
export interface TimelineThreadMessage {
  direction: "outbound" | "inbound"
  subject: string | null
  body: string | null
  sender: string | null
  recipient: string | null
  // Parsed To/Cc lists — present for both inbound and outbound now. Null only on legacy
  // rows that predate per-email storage, where the view falls back to `recipient`.
  recipientsTo: string[] | null
  recipientsCc: string[] | null
  sentAt: string | null
}

/**
 * The actual email behind a timeline step — what was sent (sent / reminder)
 * or received (reply). Present only on events that have an email.
 */
export interface TimelineEmail {
  direction: "outbound" | "inbound"
  subject: string | null
  body: string | null
  sender: string | null
  recipient: string | null
  // Parsed To/Cc lists — present for both inbound and outbound now. Null only on legacy
  // rows, where the view falls back to the inquiry's recipients, then to `recipient`.
  recipientsTo: string[] | null
  recipientsCc: string[] | null
  sentAt: string | null
  attachments: TimelineEmailAttachment[]
  /** Earlier messages in this thread (oldest-first); populated for outbound steps. */
  thread: TimelineThreadMessage[]
}

/**
 * A supplier reply on the thread (TUR-1754). `open`: it brought no new declaration
 * (text only, re-sent copies, our own blank files) and nobody answered it or marked it
 * done. `received`: it brought documents, which carry it into review.
 */
export interface InquirySupplierMessage {
  id: string
  /** What the supplier newly wrote; the quoted history is stripped server-side. */
  text: string
  state: "open" | "received" | "answered" | "done"
}

export interface InquiryTimelineEvent {
  // Known types are listed; unknown values are humanized server-side with
  // tone "neutral", so treat `type` as an open string.
  type: string
  title: string
  // English audit text (event.notes). Fallback only: when `detailKey` is set the
  // client renders the localized detail from `timelineDetailText` instead.
  subtitle: string | null
  detailKey: string | null
  detailParams: Record<string, string>
  timestamp: string | null
  tone: InquiryTimelineTone
  link: { kind: "declaration"; ref: string } | null
  /** The row's declaration was deleted since; `link` is null then. */
  declarationDeleted?: boolean
  email: TimelineEmail | null
  /** Set on a genuine supplier reply only. */
  supplierMessage?: InquirySupplierMessage | null
}

export interface SupplierInquiryArticle {
  articleId: string
  articleNumber: string | null
  description: string | null
  supplierArticleNumber: string | null
}

export interface SupplierInquiryAttachment {
  fileKey: string
  fileName: string
  contentType: string | null
}

/**
 * Full inquiry detail. Note: `bucket`, `statusLabel`, `statusTone` are NOT
 * present here — they live on list items only. Derive the header pill from
 * `status` via INQUIRY_STATUS_DISPLAY.
 */
export interface SupplierInquiryDetail {
  id: string
  reference: string | null
  supplierId: string | null
  supplierName: string | null
  status: InquiryStatus
  frameworks: string[]
  /** As on the list item, except null on a draft no selection applies to (full documents). */
  askedRegulations?: string[] | null
  /** As on the list item, except a draft following Settings carries the org's live set. */
  regulationKeys?: string[] | null
  scopeSource?: InquiryScopeSource | null
  regulationScope?: InquiryRegulationScope | null
  triggerType: string | null
  triggerDetail: string | null
  originActor: string | null
  automationLevel: string | null
  sourceRequestId: string | null
  resolvedByDeclarationId: string | null
  dueAt: string | null
  sentAt: string | null
  closedAt: string | null
  emailCopy: Record<
    "first" | "reminder",
    Record<"en" | "de", { subject: string; body: string }>
  >
  emailTo: string | null
  recipientsTo: string[]
  recipientsCc: string[]
  language: "en" | "de" | null
  emailOperationId: string | null
  // Per-inquiry auto-reminder override: null = inherit the org setting, true = force on,
  // false = paused for this inquiry only. `autoRemindersEffective` is the resolved state
  // the toggle reflects (override if set, else the org switch).
  autoRemindersEnabled: boolean | null
  autoRemindersEffective: boolean
  // Same derivation as the list row's `hasUnprocessedReply`.
  hasUnprocessedReply: boolean
  // The regulations a partial answer left open while the inquiry is open (TUR-1732).
  openRegulations?: string[] | null
  // Same derivation as the list row's `hasOpenSupplierMessage`.
  hasOpenSupplierMessage: boolean
  emailAttachments: SupplierInquiryAttachment[]
  articles: SupplierInquiryArticle[]
  timeline: InquiryTimelineEvent[]
  questionnaire: Record<string, Record<string, string>>
  /** Active warning notes of this inquiry's supplier (MC_NOTES). Empty, or absent on an
   *  older response, when the flag is off. */
  supplierWarnings?: NoteOut[]
}

export const INQUIRY_STATUS_DISPLAY: Record<
  InquiryStatus,
  { labelKey: TranslationKeys; tone: InquiryPillTone }
> = {
  draft: { labelKey: "MC_INQ_STATUS_PILL_DRAFT", tone: "muted" },
  waiting: { labelKey: "MC_INQ_STATUS_PILL_WAITING", tone: "info" },
  reminded: { labelKey: "MC_INQ_STATUS_PILL_REMINDED", tone: "attention" },
  escalated: { labelKey: "MC_INQ_STATUS_PILL_ESCALATED", tone: "danger" },
  ureview: { labelKey: "MC_INQ_STATUS_PILL_UREVIEW", tone: "success" },
  answered: { labelKey: "MC_INQ_STATUS_PILL_ANSWERED", tone: "success" },
  // Deliberately closed without an answer — muted, distinct from the success "answered".
  closed: { labelKey: "MC_INQ_STATUS_PILL_CLOSED", tone: "muted" },
  undeliverable: {
    labelKey: "MC_INQ_STATUS_PILL_UNDELIVERABLE",
    tone: "danger",
  },
  complained: {
    labelKey: "MC_INQ_STATUS_PILL_COMPLAINED",
    tone: "danger",
  },
}

/** Localized pill label for a fine inquiry status; unknown values (a newer backend)
 *  fall back to the raw status so a row never renders blank. */
export function inquiryStatusLabel(
  status: string,
  translate: (key: TranslationKeys) => string,
): string {
  const display = INQUIRY_STATUS_DISPLAY[status as InquiryStatus]
  return display ? translate(display.labelKey) : status
}

// ─── List ───────────────────────────────────────────────────────────────────

export interface InquiryListParams {
  bucket?: InquiryBucket
  // Comma list of fine statuses (from toFineStatusList), ANDed with bucket on
  // the backend. Mutually exclusive with `bucket` in practice — the panel
  // sends one or the other, never both.
  status?: string
  q?: string
  supplierId?: string
  // Comma list of canonical framework keys (INQUIRY_REGULATION_KEYS); matches an
  // inquiry asking any of them (422 on an unknown key).
  regulation?: string
  // manual | customer_gap | stale_data | svhc_update | declaration_review —
  // validated server-side (400 on an unknown value). Wire key is
  // `trigger_type`, same manual snake_case rename as supplierId.
  triggerType?: string
  overdue?: boolean
  /** name_asc|name_desc|created_asc|created_desc — "name" sorts by supplier name. */
  sort?: string
  limit?: number
  enabled?: boolean
}

export function useInquiryList(params: InquiryListParams = {}) {
  const { enabled = true, ...listParams } = params
  return useInfiniteQuery({
    queryKey: ["mc", "inquiries", "list", listParams],
    queryFn: ({ pageParam }) => {
      const { supplierId, triggerType, ...rest } = listParams
      return apiClientV2
        .get<PaginatedOut<SupplierInquiryListItem>>(BASE, {
          params: {
            ...rest,
            supplier_id: supplierId,
            trigger_type: triggerType,
            cursor: pageParam ?? undefined,
          },
        })
        .then((r) => ({
          // See useDeclarationList's identical guard (declarationsApi.ts):
          // an empty-bodied 2xx resolves `r.data` to `""`, and every caller
          // flatMaps `.items` across pages with no null check. `total`
          // (InquiryListPanel reads pages[0].total) gets the same fallback.
          items: r.data?.items ?? [],
          total: r.data?.total ?? 0,
          nextCursor: r.data?.nextCursor ?? null,
        }))
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
    // Keep the previous page's rows on screen while a new search/filter
    // combination loads, instead of flashing to the loading skeleton.
    placeholderData: keepPreviousData,
  })
}

// ─── Stats (tab counts) ─────────────────────────────────────────────────────

export function useInquiryStats() {
  return useQuery({
    queryKey: ["mc", "inquiries", "stats"],
    queryFn: () =>
      apiClientV2
        .get<SupplierInquiryStats>(`${BASE}/stats`)
        .then((r) => r.data),
  })
}

/** One keyset page of the Needs-matching set. No total by design — see the backend schema. */
export interface InquiryMatchCandidatesPage {
  items: InquiryMatchCandidate[]
  nextCursor: string | null
}

interface InquiryMatchCandidatesPageResponse {
  items: InquiryMatchCandidateResponse[]
  nextCursor: string | null
}

export function useInquiryMatchCandidates(enabled = true, q?: string) {
  return useInfiniteQuery({
    queryKey: ["mc", "inquiries", "matches", q ?? ""],
    queryFn: ({ pageParam }): Promise<InquiryMatchCandidatesPage> =>
      apiClientV2
        .get<InquiryMatchCandidatesPageResponse>(`${BASE}/matches`, {
          params: {
            cursor: pageParam ?? undefined,
            limit: 50,
            q: q || undefined,
          },
        })
        .then((r) => ({
          // Same empty-bodied-2xx guard as useInquiryList/useDeclarationList:
          // `r.data` can resolve to `""`, and `.items.map(...)` on that threw
          // "Cannot read properties of undefined (reading 'map')" before this.
          nextCursor: r.data?.nextCursor ?? null,
          items: (r.data?.items ?? []).map((candidate) => ({
            ...candidate,
            receivedAt: new Date(candidate.receivedAt),
          })),
        })),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
    // Keep the current results on screen while a new search loads, as the other lists do.
    placeholderData: keepPreviousData,
  })
}

export function useConnectInquiryMatch() {
  const qc = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: ({
      candidateId,
      inquiryId,
    }: {
      candidateId: string
      inquiryId: string
    }) =>
      apiClientV2
        .post<SupplierInquiryDetail>(`${BASE}/matches/${candidateId}/connect`, {
          inquiryId,
        })
        .then((r) => r.data),
    onSuccess: (inquiry) => {
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "matches"] })
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "stats"] })
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "list"] })
      qc.invalidateQueries({
        queryKey: ["mc", "inquiries", "full-detail", inquiry.id],
      })
    },
    onError: (error) => {
      toastApiError(error, translate, "MC_API_ERROR_GENERIC")
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_SEND)
}

export function useResolveInquiryMatch() {
  const qc = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: ({
      candidateId,
      resolution,
    }: {
      candidateId: string
      resolution: "standalone" | "dismissed"
    }) =>
      apiClientV2
        .post(`${BASE}/matches/${candidateId}/resolve`, {
          resolution,
        })
        .then(() => undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "matches"] })
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "stats"] })
    },
    onError: (error) => {
      toastApiError(error, translate, "MC_API_ERROR_GENERIC")
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_SEND)
}

// ─── Frameworks (checkbox options for create/update) ────────────────────────

export interface FrameworkOption {
  // `value` equals the label and is the stable wire token used in frameworks[].
  value: string
  label: string
  description: string | null
}

function fetchInquiryFrameworks(): Promise<FrameworkOption[]> {
  return apiClientV2
    .get<FrameworkOption[]>(`${BASE}/frameworks`)
    .then((r) => r.data)
}

/**
 * Shared so a non-React caller resolves frameworks through the same cache the
 * picker uses — the agent chat sends the full list, as NewInquiryPage does.
 */
export const inquiryFrameworksQuery = {
  queryKey: ["mc", "inquiries", "frameworks"],
  queryFn: fetchInquiryFrameworks,
}

export function useInquiryFrameworks() {
  return useQuery(inquiryFrameworksQuery)
}

// ─── Regulation picker options (TUR-1730) ───────────────────────────────────────

/** One regulation the inquiry picker offers. */
export interface InquiryRegulationOption {
  /** FRAMEWORK_ORDER key (REACH_SVHC, PFAS, …), labelled with regulationLabel. */
  key: string
  /** In Settings › Regelwerke: preselected. */
  inOrgSet: boolean
  /** An inquiry may ask it: the org's set, or a catalogue regulation ready for inquiries. */
  pickable: boolean
}

export interface InquiryRegulationOptions {
  /** False: MC_REGULATION_SETTINGS is off for the org. There is no picker. */
  settingsOn: boolean
  regulations: InquiryRegulationOption[]
}

/** Shared so the assistant resolves the org set through the same cache. */
export const inquiryRegulationOptionsQuery = {
  queryKey: ["mc", "inquiries", "regulation-options"],
  queryFn: () =>
    apiClientV2
      .get<InquiryRegulationOptions>(`${BASE}/regulations`)
      .then((r) => r.data),
}

export function useInquiryRegulationOptions() {
  return useQuery(inquiryRegulationOptionsQuery)
}

// ─── Detail (full, with timeline) ───────────────────────────────────────────

export function useInquiryFullDetail(id: string | null) {
  return useQuery({
    queryKey: ["mc", "inquiries", "full-detail", id],
    queryFn: () =>
      apiClientV2
        .get<SupplierInquiryDetail>(`${BASE}/${id}`)
        .then((r) => r.data),
    enabled: !!id,
  })
}

// ─── Close (POST /{id}/close) ────────────────────────────────────────────────

/** The optional parts of the close body. `noteId` names an inquiry note written
 *  just before: its text becomes the reason on the timeline (MC_NOTES). */
export interface CloseInquiryBody {
  noteId?: string
}

export function useCloseInquiry() {
  const qc = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: ({ id, body = {} }: { id: string; body?: CloseInquiryBody }) =>
      // Backend requires a JSON body (SupplierInquiryCloseIn); send {} when there is
      // nothing to add, since every field (resolved_by_declaration_id, note_id) is
      // optional. Omitting the body 422s.
      apiClientV2
        .post<SupplierInquiryDetail>(`${BASE}/${id}/close`, body)
        .then((r) => r.data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "full-detail", id] })
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "list"] })
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "stats"] })
    },
    onError: (error, { id, body }) => {
      // A close that carried a note and got a 409 lost a race: the note is saved, the
      // inquiry was closed (or answered) by someone else in the meantime. Say so, and
      // refresh what the screen still shows as open.
      if (body?.noteId && getApiErrorStatus(error) === 409) {
        toast.error(translate("MC_INQ_CLOSE_ALREADY_CLOSED"))
        invalidateInquiryCaches(qc, id)
        return
      }
      toastApiError(error, translate, "MC_API_ERROR_GENERIC")
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_SEND)
}

// ─── Accept a held declaration as the answer (POST /{id}/accept-declaration/{declId}) ──
// The email path holds a document it could not vouch for (unreadable scan, blank
// template) as an "additional declaration" and the inquiry keeps waiting; nothing
// automatic ever promotes it. This is the reviewer's release (TUR-1473).
export function useAcceptInquiryDeclaration() {
  const qc = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: ({
      inquiryId,
      declarationId,
    }: {
      inquiryId: string
      declarationId: string
    }) =>
      apiClientV2
        .post<SupplierInquiryDetail>(
          `${BASE}/${inquiryId}/accept-declaration/${declarationId}`,
        )
        .then((r) => r.data),
    onSuccess: (_data, { inquiryId }) => {
      qc.invalidateQueries({
        queryKey: ["mc", "inquiries", "full-detail", inquiryId],
      })
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "list"] })
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "stats"] })
    },
    onError: (error, { inquiryId }) => {
      // Deleted after the timeline loaded: say so, and refetch so the row drops its buttons.
      const deleted = getApiErrorCode(error) === "DECLARATION_NOT_FOUND"
      if (deleted)
        qc.invalidateQueries({
          queryKey: ["mc", "inquiries", "full-detail", inquiryId],
        })
      toastApiError(
        error,
        translate,
        deleted ? "MC_INQ_ACCEPT_DECLARATION_DELETED" : "MC_API_ERROR_GENERIC",
      )
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_SEND)
}

// ─── Supplier messages (TUR-1754) ────────────────────────────────────────────
// Answer a supplier reply on the inquiry's own thread, or settle it without one.

export function useAnswerSupplierMessage() {
  const qc = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: ({
      inquiryId,
      messageId,
      body,
    }: {
      inquiryId: string
      messageId: string
      body: string
    }) =>
      apiClientV2
        .post<SupplierInquiryDetail>(
          `${BASE}/${inquiryId}/messages/${messageId}/answer`,
          { body },
        )
        .then((r) => r.data),
    onSuccess: (_data, { inquiryId }) => invalidateInquiryCaches(qc, inquiryId),
    onError: (error, { inquiryId }) => {
      toastApiError(error, translate, "MC_API_ERROR_GENERIC")
      // A 409 means someone else answered first: show what the timeline says now.
      if (getApiErrorStatus(error) === 409)
        invalidateInquiryCaches(qc, inquiryId)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_SEND)
}

export function useMarkSupplierMessageDone() {
  const qc = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: ({
      inquiryId,
      messageId,
    }: {
      inquiryId: string
      messageId: string
    }) =>
      apiClientV2
        .post<SupplierInquiryDetail>(
          `${BASE}/${inquiryId}/messages/${messageId}/done`,
        )
        .then((r) => r.data),
    onSuccess: (_data, { inquiryId }) => invalidateInquiryCaches(qc, inquiryId),
    onError: (error) => {
      toastApiError(error, translate, "MC_API_ERROR_GENERIC")
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_VIEW)
}

// ─── Auto-reminders override (PATCH /{id}/auto-reminders) ────────────────────
// Tri-state per-inquiry override on the org reminder switch:
//   "off"     = quiet pause for this inquiry only (no reminder, no hand-back),
//   "inherit" = clear the override, follow the org default (how "resume" returns to normal),
//   "on"      = force reminders on even if the org switch is off.
export type AutoReminderMode = "inherit" | "on" | "off"

export function useSetAutoReminders() {
  const qc = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: AutoReminderMode }) =>
      apiClientV2
        .patch<SupplierInquiryDetail>(`${BASE}/${id}/auto-reminders`, {
          mode,
        })
        .then((r) => r.data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "full-detail", id] })
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "list"] })
    },
    onError: (error) => {
      toastApiError(error, translate, "MC_API_ERROR_GENERIC")
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_REMIND)
}

/**
 * Drop the caches every inquiry surface reads, for a write outside React's render
 * tree (the *Direct calls below have no onSuccess). A mounted panel does not
 * refetch on its own.
 */
export function invalidateInquiryCaches(qc: QueryClient, id?: string): void {
  if (id) {
    qc.invalidateQueries({ queryKey: ["mc", "inquiries", "full-detail", id] })
    qc.invalidateQueries({ queryKey: ["mc", "inquiries", "detail", id] })
  }
  qc.invalidateQueries({ queryKey: ["mc", "inquiries", "list"] })
  qc.invalidateQueries({ queryKey: ["mc", "inquiries", "stats"] })
}

// Direct calls for callers outside React's render tree (the agent-chat tool
// executor). Same endpoints as the hooks above, without the toast wiring a
// component wants — the chat reports the outcome to the model instead — and with
// the cache work asked for explicitly via invalidateInquiryCaches.
export async function closeInquiryDirect(
  id: string,
  body: CloseInquiryBody = {},
): Promise<SupplierInquiryDetail> {
  return (
    apiClientV2
      // The body is required (SupplierInquiryCloseIn) even when empty; omitting it 422s.
      // No resolvedByDeclarationId: the app's own Close never sets one either.
      .post<SupplierInquiryDetail>(`${BASE}/${id}/close`, body)
      .then((r) => r.data)
  )
}

/** One inquiry, for a pre-write check outside React's render tree. */
export async function fetchInquiryDetail(
  id: string,
): Promise<SupplierInquiryDetail> {
  return apiClientV2
    .get<SupplierInquiryDetail>(`${BASE}/${id}`)
    .then((r) => r.data)
}

/** A `Supplier-Inquiry-…` or pre-2026-08 `Polaris-Q-…` reference, or null. */
export function inquiryReference(value: string): string | null {
  return /^\s*(supplier-inquiry|polaris-q)[\s-]/i.test(value)
    ? value.trim()
    : null
}

/**
 * The record id for whichever identifier the model is holding.
 *
 * Every inquiry surface shows the reference and never the row id, so that is
 * what a person quotes and what a model repeats. Matched exactly rather than
 * canonicalised: two prefixes are in use and the sequence width has changed, so
 * comparing against the stored string is safer than rebuilding it.
 *
 * Returns `value` unchanged when it is not a reference, or when no row carries
 * it — the endpoint's own 404 is then the honest answer.
 */
export async function resolveInquiryId(value: string): Promise<string> {
  const reference = inquiryReference(value)
  if (!reference) return value
  // Deliberately a bare request, not the list hook: that swallows errors into an
  // empty page, which on a write path would turn a network failure into "no such
  // inquiry" and send the model looking for a record that exists.
  const { data } = await apiClientV2.get<PaginatedOut<SupplierInquiryListItem>>(
    BASE,
    { params: { q: reference } },
  )
  const wanted = reference.toUpperCase()
  const hit = (data?.items ?? []).find(
    (row) => row.reference?.trim().toUpperCase() === wanted,
  )
  return hit?.id ?? value
}

export async function setAutoRemindersDirect(
  id: string,
  mode: AutoReminderMode,
): Promise<SupplierInquiryDetail> {
  return apiClientV2
    .patch<SupplierInquiryDetail>(`${BASE}/${id}/auto-reminders`, { mode })
    .then((r) => r.data)
}

// ─── Discard a draft (DELETE /{id}) ──────────────────────────────────────────
// Draft-only on the backend (a sent inquiry is Closed, not discarded). Soft-deletes
// the draft so it drops out of the list.
export function useDeleteInquiry() {
  const qc = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: (id: string) =>
      apiClientV2.delete(`${BASE}/${id}`).then(() => undefined),
    onSuccess: (_data, id) => {
      // The draft no longer exists: drop its cached detail rather than refetching
      // it into a 404 (as the bulk discard does), and take its row out of every
      // cached list now. The detail panel clears `?id=` right after this, and the
      // list's default selection (useDefaultRowSelection) would otherwise pick the
      // first row of the list still in cache, this very draft, before the refetch
      // lands.
      qc.removeQueries({ queryKey: ["mc", "inquiries", "full-detail", id] })
      qc.setQueriesData<InfiniteData<{ items: SupplierInquiryListItem[] }>>(
        { queryKey: ["mc", "inquiries", "list"] },
        (data) =>
          data && {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              items: page.items.filter((row) => row.id !== id),
            })),
          },
      )
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "list"] })
      qc.invalidateQueries({ queryKey: ["mc", "inquiries", "stats"] })
    },
    onError: (error) => {
      toastApiError(error, translate, "MC_API_ERROR_GENERIC")
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_INQUIRIES_SEND)
}

// Direct discard — used for bulk fan-out, mirroring sendInquiryDirect: firing N
// concurrent calls on a single react-query mutation instance makes it serialize/
// interfere, so bulk callers use this plain request and invalidate caches once at
// the end. Draft-only on the backend (204; a non-draft returns 409).
export async function discardInquiryDirect(id: string): Promise<void> {
  await apiClientV2.delete(`${BASE}/${id}`)
}
